import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, ImageOff, ImagePlus } from 'lucide-react';
import Button from '../../common/Button';
import FormField, { Input } from '../../common/FormField';
import { jobPhotoAPI } from '../../../services/api';
import PhotoViewer, { formatAdded, openBlob } from './PhotoViewer';
import { downscaleImage } from '../../../lib/downscaleImage';
import { fetchPhotoBlob } from '../../../lib/fetchPhotoBlob';

const TOO_LARGE = 'This photo is too large (max 15 MB)';
const OFFLINE = 'Upload failed — check your connection and try again';
const GENERIC = "Couldn't save the photo";

// Thumbnails are fetched through an authenticated request, so a job with many
// photos must not fire them all at once. At most THUMB_CONCURRENCY run at a
// time; the rest wait in this queue (and drop out of it if their tile unmounts).
const THUMB_CONCURRENCY = 2;
const thumbQueue = [];
let thumbActive = 0;

function startThumb(task) {
  thumbActive += 1;
  task().finally(() => {
    thumbActive -= 1;
    runThumbQueue();
  });
}

function runThumbQueue() {
  while (thumbActive < THUMB_CONCURRENCY && thumbQueue.length) {
    startThumb(thumbQueue.shift());
  }
}

function enqueueThumb(task) {
  thumbQueue.push(task);
  runThumbQueue();
  return () => {
    const i = thumbQueue.indexOf(task);
    if (i >= 0) thumbQueue.splice(i, 1);
  };
}

export function uploadErrorMessage(err) {
  const status = err?.response?.status;
  if (status === 413) return TOO_LARGE;
  if (!err?.response) return OFFLINE;
  const detail = err.response.data?.detail;
  if (typeof detail === 'string') return detail;
  return GENERIC;
}

function PhotoTile({ jobId, photo, onMedia, onView }) {
  const [media, setMedia] = useState(null); // {blob, url}
  const [blobFailed, setBlobFailed] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined');
  const tileRef = useRef(null);
  const onMediaRef = useRef(onMedia);
  onMediaRef.current = onMedia;

  // Only tiles near the viewport fetch; a long job would otherwise fire dozens
  // of requests at once and trip the server's rate limit.
  useEffect(() => {
    if (visible) return undefined;
    const el = tileRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;
    let url = null;
    const token = { cancelled: false };
    setBlobFailed(false);
    setDecodeFailed(false);
    const cancelQueued = enqueueThumb(async () => {
      try {
        const blob = await fetchPhotoBlob(jobId, photo.id, token);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setMedia({ blob, url });
        onMediaRef.current(photo.id, { blob, url });
      } catch (e) {
        if (!cancelled) setBlobFailed(true);
      }
    });
    return () => {
      cancelled = true;
      token.cancelled = true;
      if (token.wake) token.wake();
      cancelQueued();
      if (url) {
        onMediaRef.current(photo.id, null);
        URL.revokeObjectURL(url);
      }
    };
  }, [jobId, photo.id, attempt, visible]);

  const added = formatAdded(photo.created_at);

  let body;
  if (blobFailed) {
    body = (
      <div className="text-center space-y-1 p-2">
        <ImageOff size={24} strokeWidth={1.5} className="text-ink-faint mx-auto" aria-hidden="true" />
        <p className="text-small text-ink-muted">Couldn't load photo</p>
        <div className="flex gap-2 justify-center">
          <Button variant="secondary" size="sm" onClick={() => setAttempt((a) => a + 1)}>Retry</Button>
          <Button variant="secondary" size="sm" onClick={() => onView(photo.id)}>Details</Button>
        </div>
      </div>
    );
  } else if (media && decodeFailed) {
    body = (
      <div className="text-center space-y-1 p-2">
        <ImageOff size={24} strokeWidth={1.5} className="text-ink-faint mx-auto" aria-hidden="true" />
        <p className="text-small text-ink-muted">Preview not available</p>
        <div className="flex gap-2 justify-center">
          <Button variant="secondary" size="sm" onClick={() => openBlob(media.blob, photo)}>Open</Button>
          <Button variant="secondary" size="sm" onClick={() => onView(photo.id)}>Details</Button>
        </div>
      </div>
    );
  } else if (media) {
    body = (
      <img
        src={media.url}
        alt={photo.note || 'Job photo'}
        className="w-full h-full object-cover"
        onError={() => setDecodeFailed(true)}
      />
    );
  } else {
    body = <ImageOff size={24} strokeWidth={1.5} className="text-ink-faint" aria-hidden="true" />;
  }

  const showButton = !blobFailed && !decodeFailed;

  return (
    <li ref={tileRef} className="rounded-card border border-border-default bg-surface-card overflow-hidden">
      <div className="aspect-[4/3] bg-surface-sunken flex items-center justify-center">
        {showButton ? (
          <button
            type="button"
            aria-label="View photo"
            className="w-full h-full flex items-center justify-center"
            onClick={() => onView(photo.id)}
          >
            {body}
          </button>
        ) : body}
      </div>
      <div className="p-3 space-y-1">
        {photo.note && <p className="text-body-strong text-ink break-words">{photo.note}</p>}
        <p className="text-small text-ink-muted">
          {added}
          {photo.taken_by_name ? ` · ${photo.taken_by_name}` : ''}
        </p>
      </div>
    </li>
  );
}

export default function JobPhotos({ jobId }) {
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pending, setPending] = useState([]); // File[] waiting to be saved
  const [total, setTotal] = useState(0); // size of the current batch
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [media, setMedia] = useState({}); // photo id -> {blob, url} already loaded by its tile
  const sourceRef = useRef('camera');
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const mounted = useRef(true);
  const savingRef = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    jobPhotoAPI.list(jobId)
      .then((data) => { if (!cancelled) { setPhotos(data.photos || []); setLoaded(true); } })
      .catch(() => { if (!cancelled) { setLoadError("Couldn't load photos"); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [jobId]);

  // Preview the first pending photo; revoke its URL when it changes.
  const first = pending[0];
  useEffect(() => {
    if (!first) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(first);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [first]);

  const onPick = useCallback((source) => (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // lets the same file be picked again
    if (!files.length) return;
    sourceRef.current = source;
    setPending(files);
    setTotal(files.length);
    setError('');
  }, []);

  const clearPending = () => {
    setPending([]);
    setTotal(0);
    setNote('');
    setError('');
  };

  const retake = () => {
    const input = sourceRef.current === 'gallery' ? galleryRef.current : cameraRef.current;
    setPending([]);
    setTotal(0);
    setError('');
    if (input) input.click();
  };

  // After a quick-fill the cursor goes to the end of the note, so a gloved
  // mechanic taps "Part number" and types the number without a second tap.
  const quickFill = (prefix) => {
    setNote((n) => (n.startsWith(prefix) ? n : prefix + n));
    setTimeout(() => {
      const input = document.getElementById('job-photo-note');
      if (!input) return;
      input.focus();
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 0);
  };

  const save = async () => {
    if (savingRef.current || !pending.length) return;
    savingRef.current = true;
    setSaving(true);
    setError('');
    let remaining = pending;
    const done = total - pending.length;
    let index = done;
    try {
      while (remaining.length) {
        const f = remaining[0];
        setProgress(0);
        try {
          const { blob, filename } = await downscaleImage(f, {});
          const saved = await jobPhotoAPI.upload(jobId, blob, filename, note.trim(), (p) => {
            if (mounted.current) setProgress(p);
          });
          if (!mounted.current) return;
          setPhotos((prev) => [saved, ...prev]);
          remaining = remaining.slice(1);
          index += 1;
          setPending(remaining);
        } catch (err) {
          if (!mounted.current) return;
          const msg = uploadErrorMessage(err);
          setError(total > 1 ? `Photo ${index + 1} of ${total} failed: ${msg}` : msg);
          return;
        }
      }
      if (mounted.current) clearPending();
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const hasPending = pending.length > 0;

  const onMedia = useCallback((id, m) => {
    if (!mounted.current) return;
    setMedia((prev) => {
      if (!m) {
        if (!(id in prev)) return prev;
        const { [id]: _gone, ...rest } = prev;
        return rest;
      }
      return { ...prev, [id]: m };
    });
  }, []);

  const onSaved = useCallback((updated) => {
    setPhotos((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
  }, []);

  const onDeleted = useCallback((id, nextId) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
    setViewId(nextId);
  }, []);

  return (
    <section aria-labelledby="job-photos-heading" className="space-y-4">
      <h5 id="job-photos-heading" className="text-card-title text-ink">
        Photos ({photos.length})
      </h5>

      {!hasPending && (
        <div className="space-y-2">
          <Button className="w-full" icon={Camera} onClick={() => cameraRef.current?.click()}>
            Take photo
          </Button>
          <Button className="w-full" variant="secondary" icon={ImagePlus} onClick={() => galleryRef.current?.click()}>
            Add from gallery
          </Button>
        </div>
      )}
      <input
        ref={cameraRef}
        data-testid="take-photo-input"
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick('camera')}
      />
      <input
        ref={galleryRef}
        data-testid="gallery-input"
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onPick('gallery')}
      />

      {hasPending && (
        <div className="rounded-card border border-border-default bg-surface-card p-3 space-y-3">
          <div className="aspect-[4/3] bg-surface-sunken rounded-control overflow-hidden flex items-center justify-center">
            {previewUrl ? (
              <img src={previewUrl} alt="Preview of the new capture" className="w-full h-full object-contain" />
            ) : null}
          </div>
          {total > 1 && (
            <p className="text-small text-ink-muted">
              Photo {total - pending.length + 1} of {total}. The same note is used for each.
            </p>
          )}
          <FormField label="Note (optional)" htmlFor="job-photo-note">
            <Input
              id="job-photo-note"
              value={note}
              maxLength={500}
              disabled={saving}
              onChange={(e) => setNote(e.target.value)}
            />
          </FormField>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => quickFill('Pre-existing damage: ')}>
              Pre-existing damage
            </Button>
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => quickFill('Part number: ')}>
              Part number
            </Button>
          </div>

          {saving && (
            <div>
              <p className="text-small text-ink-muted" aria-live="polite">
                Uploading… {Math.round(progress * 100)}%
              </p>
              <div className="h-1.5 rounded-full bg-surface-sunken overflow-hidden">
                <div className="h-full bg-accent" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-control border border-danger-border bg-danger-tint p-3 space-y-2">
              <p className="text-body text-danger">{error}</p>
              <Button variant="secondary" size="sm" onClick={save}>Try again</Button>
            </div>
          )}

          <div className="flex gap-2">
            <Button className="flex-1" loading={saving} onClick={save}>Save</Button>
            <Button className="flex-1" variant="secondary" disabled={saving} onClick={retake}>Retake</Button>
          </div>
        </div>
      )}

      {loadError && <p role="alert" className="text-body text-danger">{loadError}</p>}

      {loaded && !loadError && photos.length === 0 && (
        <p className="text-body text-ink-muted">No photos yet. Take one of any damage or part numbers.</p>
      )}

      {photos.length > 0 && (
        <ul className="grid grid-cols-2 gap-3">
          {photos.map((p) => (
            <PhotoTile key={p.id} jobId={jobId} photo={p} onMedia={onMedia} onView={setViewId} />
          ))}
        </ul>
      )}

      {viewId !== null && (
        <PhotoViewer
          jobId={jobId}
          photos={photos}
          currentId={viewId}
          media={media}
          onChange={setViewId}
          onClose={() => setViewId(null)}
          onSaved={onSaved}
          onDeleted={onDeleted}
        />
      )}
    </section>
  );
}
